import {
    Component,
    ComponentRef,
    Injector,
    Input,
    OnDestroy,
    QueryList,
    ViewContainerRef,
} from '@angular/core';

import { UiComponent } from '../../models/ui-component.models';
import { DynamicFacade } from '../../store/dynamic.facade';
import { DynamicUiService } from '../../services/dynamic-ui.service';
import { Observable, Subject, Subscription, distinctUntilChanged, of, takeUntil } from 'rxjs';
import isEqual from 'lodash-es/isEqual';

@Component({
  standalone: false,
    template: '',
})
export abstract class DynamicBaseClass implements OnDestroy {
    @Input() config!: any;
    @Input() children!: UiComponent[];
    @Input() storeDataChildNames: string[] = [];

    protected dynamicUiServices: DynamicUiService;
    protected dynamicFacade: DynamicFacade;
    protected viewCondition: boolean = true;
    public isLoading: boolean = false;
    protected subscription$: Subscription = new Subscription();
    protected destroy$ = new Subject<void>();
    protected alwaysRefreshStoreData: boolean = false;

    constructor(private injector: Injector) {
        this.dynamicUiServices = injector.get(DynamicUiService);
        this.dynamicFacade = injector.get(DynamicFacade);
    }

    ngOnDestroy(): void {
        this.subscription$.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected AddComponentsFromChildren(
        viewChildrenRef: QueryList<ViewContainerRef>,
        componentRef: ComponentRef<DynamicBaseClass>
    ) {
        viewChildrenRef.map((vcr: ViewContainerRef, index: number) => {
            vcr.clear();
            const component = this.dynamicUiServices.GetComponent(
                this.children[index]
            )?.component;
            if (component) {
                componentRef = vcr.createComponent(component);
                componentRef.instance.children = this.children[index].children;
                componentRef.instance.config = this.children[index].config;
                componentRef.instance.config = this.children[index].config;
                componentRef.instance.storeDataChildNames = this.storeDataChildNames;
            }
        });
    }

    protected GetValueFromConfig(): Observable<any> {
        if (this.config) {
            if (this.config.sourceStoreData) {
                return this.dynamicUiServices.GetValueAsObservable(this.config.sourceStoreData, this.storeDataChildNames, this.alwaysRefreshStoreData).pipe(takeUntil(this.destroy$));
            } else if (this.config.storeData) {
                return this.dynamicUiServices.GetValueAsObservable(this.config.storeData, this.storeDataChildNames, this.alwaysRefreshStoreData).pipe(takeUntil(this.destroy$));
            } else if (this.config.staticValue) {
                return of(this.config.staticValue);
            }
        }
        return of('');
    }

    protected GetTitleFromConfig(): Observable<string> {
        if (this.config) {
            if (this.config.title) {
                return of(this.config.title).pipe(takeUntil(this.destroy$), distinctUntilChanged());
            }
        }
        return of('');
    }

    protected GetSubtitleFromConfig(): Observable<string> {
        if (this.config) {
            if (this.config.subtitle) {
                return of(this.config.subtitle).pipe(takeUntil(this.destroy$), distinctUntilChanged());
            }
        }
        return of('');
    }

    protected GetDescriptionFromConfig(): Observable<string> {
        if (this.config) {
            if (this.config.description) {
                return of(this.config.description).pipe(takeUntil(this.destroy$), distinctUntilChanged());
            }
        }
        return of('');
    }

    protected GetColorFromConfig(): Observable<string> {
        if (this.config) {
            if (this.config.color) {
                return of(this.config.color).pipe(takeUntil(this.destroy$), distinctUntilChanged());
            }
        }
        return of('');
    }

    protected GetClassFromConfig(defaultClass: string = undefined): Observable<string> {
        if (this.config) {
            if (this.config.class) {
                return of(this.config.class).pipe(takeUntil(this.destroy$), distinctUntilChanged());
            }
        }
        return of(defaultClass);
    }

    protected GetUrlFromConfig(): Observable<string> {
        if (this.config) {
            if (this.config.staticUrl) {
                return of(this.config.staticUrl).pipe(takeUntil(this.destroy$), distinctUntilChanged());
            }
        }
        return of('');
    }

    SetValueFromConfig(newValue: string) {
        if (this.config) {
            if (this.config.targetStoreData) {
                this.dynamicUiServices.SetValue(this.config.targetStoreData, this.storeDataChildNames, newValue);
            } else if (this.config.storeData) {
                this.dynamicUiServices.SetValue(this.config.storeData, this.storeDataChildNames, newValue);
            }
        }
    }

    ConvertIcon(icon: string): string {
        if (icon) {
            switch (icon.toLowerCase()) {
                case "action": { return "flash_on" };
                case "control": { return "event_outline" };
                case "anlass": { return "event" };
                case "event": { return "emoji_events" };
                case "person": { return "person" };
                case "group": { return "group" };
                case "rangliste": { return "format_list_numbered" };
                case "praesentation": { return "dvr" };
            }
        }
        return null;
    }

    protected GlobalConfig() {
        if (this.config.viewCondition) {
            this.viewCondition = this.executeViewConditionEval(
                this.config.viewCondition,
                (c: any) => {
                    this.viewCondition = this.executeViewConditionEval(this.config.viewCondition);
                });
        }
        if (this.config.isLoadingOnExecutingActionNames) {
            this.config.isLoadingOnExecutingActionNames.forEach((actionName: string) => {
                this.dynamicUiServices
                    .GetValueAsObservable("local.executingActions." + actionName.toLowerCase(), this.storeDataChildNames)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe((isLoading: boolean) => {
                        this.isLoading = isLoading ?? false;
                    })
            });
        }
        if (this.config.alwaysRefreshStoreData) {
            this.alwaysRefreshStoreData = this.config.alwaysRefreshStoreData;
        }
    }

    protected executeViewConditionEval(viewConditionEval: string, action: (c: boolean) => void = null): boolean {
        // Abwärtskompatibilität
        let viewConditionEval2 = viewConditionEval.replace(/this\.getDataStore\(/g, "getDataStore(");

        let dynamicUiServices = this.dynamicUiServices;
        let destroy$ = this.destroy$;
        let storeDataChildNames = this.storeDataChildNames;

        function getDataStore(dataStore: string): any {
            if (action != null) {
                dynamicUiServices.GetValueAsObservable(dataStore, storeDataChildNames)
                    .pipe(
                        takeUntil(destroy$)
                    )
                    .subscribe(action);
            }
            return dynamicUiServices.GetValue(dataStore, storeDataChildNames);
        }

        // eslint-disable-next-line no-eval
        let a = eval(viewConditionEval2) as boolean;
        return a;
    }

    protected isHidden(element: HTMLElement | null): boolean {
        if (!element) {
            return false; // Kein Element, also nicht versteckt
        }
        if (element.hidden) {
            return true; // Aktuelles Element ist versteckt
        }
        return this.isHidden(element.parentElement); // Übergeordnetes Element überprüfen
    }

    protected executeAction(executeActionName: string, actionPath: String) {
        this.dynamicFacade.ExecuteAction(executeActionName, this.storeDataChildNames, actionPath);
    }
}


